import client from '@lib/core/axios.client';
import { API_ENDPOINTS } from '@lib/constants';
import type {
  MySubscriptionResponse,
  SubscriptionHistoryItem,
  SubscriptionHistoryParams,
  FeatureAccess,
  FeatureUsage,
  SubscriptionPlan,
  SubscriptionCatalog,
  EnterpriseLeadRequest,
  EnterpriseLeadResponse,
  PlanFeature,
  PlanComparison,
  RuntimeQuotaUsage,
  CustomerUsageActivity,
} from '@/types/api/subscription.types';

/**
 * Subscription Service
 * Manages all subscription-related operations
 */
export class SubscriptionService {
  private static instance: SubscriptionService;

  private constructor() {}

  public static getInstance(): SubscriptionService {
    if (!SubscriptionService.instance) {
      SubscriptionService.instance = new SubscriptionService();
    }
    return SubscriptionService.instance;
  }

  /**
   * Get current user's subscription details
   * @returns Current subscription information with usage summary
   */
  async getMySubscription(): Promise<MySubscriptionResponse> {
    return await client.get<MySubscriptionResponse>(
      API_ENDPOINTS.SUBSCRIPTION.MY
    );
  }

  async getRuntimeUsage(): Promise<RuntimeQuotaUsage> {
    return await client.get<RuntimeQuotaUsage>(API_ENDPOINTS.SUBSCRIPTION.RUNTIME_USAGE);
  }

  async getUsageActivities(limit = 30): Promise<CustomerUsageActivity[]> {
    return await client.get<CustomerUsageActivity[]>(
      API_ENDPOINTS.SUBSCRIPTION.USAGE_ACTIVITIES,
      { params: { limit } }
    );
  }

  /**
   * Schedule Free for the next monthly usage reset.
   * Current entitlements remain active until the returned effective date.
   */
  async scheduleFreeDowngrade(moduleApproval?: { requestId: string; impactDigest: string }): Promise<MySubscriptionResponse> {
    return await client.post<MySubscriptionResponse>(
      API_ENDPOINTS.SUBSCRIPTION.FREE_DOWNGRADE, moduleApproval
    );
  }

  /**
   * Get subscription history
   * @param params Query parameters for filtering history
   * @returns List of subscription history items
   */
  async getSubscriptionHistory(
    params?: SubscriptionHistoryParams
  ): Promise<SubscriptionHistoryItem[]> {
    return await client.get<SubscriptionHistoryItem[]>(
      API_ENDPOINTS.SUBSCRIPTION.HISTORY,
      { params }
    );
  }

  /**
   * Get all available features for current subscription
   * @returns List of feature access details
   */
  async getMyFeatures(): Promise<FeatureAccess[]> {
    return await client.get<FeatureAccess[]>(
      API_ENDPOINTS.SUBSCRIPTION.FEATURES
    );
  }

  /**
   * Get specific feature access details
   * @param featureCode Feature code to check
   * @returns Feature access details
   */
  async getFeatureByCode(featureCode: number): Promise<FeatureAccess> {
    return await client.get<FeatureAccess>(
      API_ENDPOINTS.SUBSCRIPTION.FEATURE_BY_CODE(featureCode)
    );
  }

  /**
   * Get current subscription usage for all features
   * @returns List of feature usage details
   */
  async getMyUsage(): Promise<FeatureUsage[]> {
    return await client.get<FeatureUsage[]>(
      API_ENDPOINTS.SUBSCRIPTION.USAGE
    );
  }

  /**
   * Get public subscription catalog with prices, credits, highlights, and checkout links.
   */
  async getCatalog(): Promise<SubscriptionCatalog> {
    return await client.get<SubscriptionCatalog>(
      API_ENDPOINTS.SUBSCRIPTION.CATALOG
    );
  }

  /**
   * Sends a sales-assisted Enterprise plan request.
   */
  async createEnterpriseLead(request: EnterpriseLeadRequest): Promise<EnterpriseLeadResponse> {
    return await client.post<EnterpriseLeadResponse>(
      API_ENDPOINTS.SUBSCRIPTION.ENTERPRISE_LEADS,
      request
    );
  }

  /**
   * Get usage details for specific feature
   * @param featureCode Feature code to check
   * @returns Feature usage details
   */
  async getUsageByCode(featureCode: number): Promise<FeatureUsage> {
    return await client.get<FeatureUsage>(
      API_ENDPOINTS.SUBSCRIPTION.USAGE_BY_CODE(featureCode)
    );
  }

  /**
   * Get all available subscription plans
   * @returns List of subscription plans
   */
  async getPlans(): Promise<SubscriptionPlan[]> {
    return await client.get<SubscriptionPlan[]>(
      API_ENDPOINTS.SUBSCRIPTION.PLANS
    );
  }

  /**
   * Get specific plan details
   * @param planId Plan ID to fetch
   * @returns Plan details
   */
  async getPlanById(planId: string): Promise<SubscriptionPlan> {
    return await client.get<SubscriptionPlan>(
      API_ENDPOINTS.SUBSCRIPTION.PLAN_BY_ID(planId)
    );
  }

  /**
   * Get features for a specific plan
   * @param planId Plan ID to fetch features for
   * @returns List of plan features
   */
  async getPlanFeatures(planId: string): Promise<PlanFeature[]> {
    return await client.get<PlanFeature[]>(
      API_ENDPOINTS.SUBSCRIPTION.PLAN_FEATURES(planId)
    );
  }

  /**
   * Compare multiple subscription plans
   * @returns Plan comparison data
   */
  async comparePlans(planId1: string, planId2: string): Promise<PlanComparison> {
    return await client.get<PlanComparison>(
      API_ENDPOINTS.SUBSCRIPTION.COMPARE_PLANS,
      { params: { planId1, planId2 } }
    );
  }

  /**
   * Helper method to check if a feature is accessible
   * @param featureCode Feature code to check
   * @returns True if feature is accessible
   */
  async hasFeatureAccess(featureCode: number): Promise<boolean> {
    try {
      const feature = await this.getFeatureByCode(featureCode);
      return feature.hasAccess;
    } catch (error) {
      console.error('Error checking feature access:', error);
      return false;
    }
  }

  /**
   * Helper method to check if subscription is active
   * @returns True if subscription is active
   */
  async isSubscriptionActive(): Promise<boolean> {
    try {
      const subscription = await this.getMySubscription();
      return subscription.isActive;
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return false;
    }
  }

  /**
   * Helper method to get remaining days in subscription
   * @returns Number of days remaining
   */
  async getDaysRemaining(): Promise<number> {
    try {
      const subscription = await this.getMySubscription();
      return subscription.daysRemaining;
    } catch (error) {
      console.error('Error getting days remaining:', error);
      return 0;
    }
  }

  /**
   * Helper method to check if subscription can be upgraded
   * @returns True if upgrade is available
   */
  async canUpgrade(): Promise<boolean> {
    try {
      const subscription = await this.getMySubscription();
      return subscription.canUpgrade;
    } catch (error) {
      console.error('Error checking upgrade availability:', error);
      return false;
    }
  }
}

export const subscriptionService = SubscriptionService.getInstance();
